import {inject, Injectable} from '@angular/core';
import {BsTableColDef, BsTableFilter, BsTableOption, BsTableService, PagedResult} from 'bs-components';
import {Observable, of} from 'rxjs';
import {BinLocationLight, BinType, formatBinType, LockMode} from '../../../core/models/storage/bin.model';
import {TranslateService} from '@ngx-translate/core';
import {formatEnum} from '../../../core/helpers/string.helper';
import {BinLocationService} from '../../../core/services/storage/bin-location.service';

@Injectable({
  providedIn: 'root'
})
export class AdminBinsTableService extends BsTableService<BinLocationLight> {
  private readonly binService = inject(BinLocationService);
  private readonly translate = inject(TranslateService);

  getOption(): BsTableOption {
    return {
      id: 'adminBinsTable',
      translationPrefix: 'ADMIN.BINS',
      withSearch: false
    };
  }

  getColDefs(): BsTableColDef[] {
    return [
      {
        field: 'reference',
        headerName: 'MODELS.BINS.REFERENCE'
      },
      {
        field: 'packingType',
        headerName: 'MODELS.BINS.PACKING_TYPE',
        valueFormatter: (params) => formatEnum(params.value)
      },
      {
        field: 'type',
        colId: 'binType',
        headerName: 'MODELS.BINS.TYPE',
        valueFormatter: (params) => this.translate.instant(formatBinType(params.value))
      },
      {
        field: 'rackType',
        headerName: 'MODELS.BINS.RACK_TYPE'
      },
      {
        field: 'maxLevel',
        colId: 'maxStackBinLevel',
        headerName: 'MODELS.BINS.MAX_LEVEL',
        sortable: false
      },
      {
        field: 'currentLevel',
        headerName: 'MODELS.BINS.CURRENT_LEVEL',
        sortable: false
      },
      {
        field: 'lockMode',
        headerName: 'MODELS.BINS.LOCKED',
        valueFormatter: (params) => formatEnum(params.value),
        cellClass: (params) => {
          switch (params.value) {
            case LockMode.NO_LOCK:
              return 'text-success';
            case LockMode.MANUAL_LOCKED:
              return 'text-danger bold';
            case null:
            case undefined:
              return 'text-success';
            default:
              return 'text-warning bold';
          }
        }
      },
      {
        field: 'validationCode',
        headerName: 'MODELS.BINS.VALIDATION_CODE',
        sortable: false
      }
    ];
  }

  getFilters$(): Observable<BsTableFilter[]> {
    return of([
      {
        field: 'reference',
        label: 'MODELS.BINS.REFERENCE',
        type: 'text'
      },
      {
        field: 'binTypeList',
        label: 'MODELS.BINS.TYPE',
        options: [
          this.getBinTypeOption([BinType.SERVICEABLE_FLOOR, BinType.SERVICEABLE_RACK]),
          this.getBinTypeOption([BinType.WORN_FLOOR, BinType.WORN_RACK]),
          this.getBinTypeOption([BinType.SPECIAL_WORN_FLOOR]),
        ]
      },
      {
        field: 'lockModeList',
        label: 'MODELS.BINS.LOCKED',
        options: Object.values(LockMode).map(val => {
          let color: any = 'warning';
          if (val === LockMode.NO_LOCK) color = 'success';
          if (val === LockMode.MANUAL_LOCKED) color = 'danger';
          return {id: val, value: val, label: formatEnum(val), color};
        })
      }
    ]);
  }

  private getBinTypeOption(types: BinType[]) {
    const formatType = formatBinType(types[0]);
    return {
      id: formatType,
      value: types,
      label: this.translate.instant(formatType)
    };
  }

  getData(): Observable<PagedResult<BinLocationLight>> {
    return this.binService.getAll(this.activeParams());
  }
}

